import './index.less';

import {
  Box,
  Flex,
  Icon,
  MenuDivider,
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorMode,
} from '@tonic-one/react';
import {
  SVGIcon,
  SVGIconProps,
  SphereIcon,
  UserExportOIcon,
} from '@tonic-one/react-icons';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoginUserStore } from '../../../models/useLoginUserModel';
import TextEllipsis from '../../Common/Tooltip/TextEllipsis';
import LanguageSetting from './LanguageSetting';
import { useTextButtonStyle } from '../style';
import { useMenusItemStyle } from './style';
import { AccountApi } from '@Apis';
import { isDefaultUI, isInUIC, logoutCleanUp } from '@Utils/common';
import AccountSwitch from '@Components/CasHeader/AccountSwitch';
import { useMultiTenantsStore } from '@Models/useMultiTenant';
import { CasAccountType } from '@Types/app';

function AccountIcon({ ...rest }: SVGIconProps) {
  return (
    <SVGIcon viewBox="0 0 24 24" {...rest}>
      <path
        d="M24 0H0V24H24V0Z"
        fill={rest.color === 'dark' ? '#5E5E5E' : '#E0E0E0'}
      />
      <path
        d="M16.14 12.96C15.21 12.36 14.25 12 13.26 11.82C14.97 11.28 16.2 9.68999 16.2 7.79999C16.2 5.48999 14.31 3.59999 12 3.59999C9.69002 3.59999 7.80002 5.48999 7.80002 7.79999C7.80002 9.68999 9.03002 11.28 10.74 11.82C9.75002 12 8.76002 12.39 7.86002 12.96C6.36002 13.89 5.40002 15.99 5.40002 18.3V24H6.60002V22.8V21.6V18.3C6.60002 16.41 7.35002 14.7 8.49002 13.98C9.60002 13.26 10.8 12.9 12 12.9C13.2 12.9 14.4 13.26 15.51 13.98C16.65 14.7 17.4 16.41 17.4 18.3V21.6V22.8V24H18.6V18.3C18.6 15.99 17.64 13.89 16.14 12.96ZM9.00002 7.79999C9.00002 6.14999 10.35 4.79999 12 4.79999C13.65 4.79999 15 6.14999 15 7.79999C15 9.44999 13.65 10.8 12 10.8C10.35 10.8 9.00002 9.44999 9.00002 7.79999Z"
        fill={rest.color === 'dark' ? 'white' : '#303030'}
        fillOpacity={rest.color === 'dark' ? '0.24' : '1'}
      />
    </SVGIcon>
  );
}

function AccountSetting() {
  const { t, i18n } = useTranslation();
  const [colorMode] = useColorMode();
  const loginUserModel = useLoginUserStore();
  const textButtonStyle = useTextButtonStyle();
  const header_t = i18n.getFixedT(null, 'header', 'header.account');
  const [languageSettingShow, setLanguageSettingShow] = useState<number>(0);
  const menusItemStyle = useMenusItemStyle();
  const { setMultiTenants } = useMultiTenantsStore();
  function openLanguageSetting() {
    setLanguageSettingShow(1);
  }
  function closeLanguageSetting() {
    setLanguageSettingShow(0);
  }

  async function logoff() {
    try {
      const res: { message: string; logoutUrl?: string } = (
        await AccountApi.logout()
      ).data;
      logoutCleanUp();
      if (res.logoutUrl) {
        window.location.href = res.logoutUrl;
        return;
      } else {
        window.location.href = '/login';
      }
    } catch (e) {
      console.error(e);
    }
  }

  const { user } = useLoginUserStore();
  useEffect(() => {
    //TODO: multiTenant
    (async function doCall() {
      const loginEmail = user ? user.loginEmail : '';
      if (loginEmail && user?.accountType === CasAccountType.LoginAccount) {
        const multiTenantsResponse = (await AccountApi.getMultiTenants()).data;
        setMultiTenants(multiTenantsResponse);
      }
    })();
  }, [loginUserModel.user]);

  const Avatar = (props: { children: ReactNode }) => (
    <Flex
      p="1x"
      borderRadius="50%"
      alignItems="center"
      justifyContent="center"
      fontSize="xs"
      lineHeight="1"
    >
      {props.children}
    </Flex>
  );

  return (
    <>
      <Popover trigger="hover" arrow={false}>
        <PopoverTrigger>
          <Flex align="center" cursor="pointer">
            <Box p="8px 8px" alignItems="center" {...textButtonStyle}>
              <Avatar>
                <AccountIcon
                  width={24}
                  height={24}
                  color={colorMode}
                  className="avatar cursor_point"
                />
              </Avatar>
            </Box>
          </Flex>
        </PopoverTrigger>

        <PopoverContent maxWidth="280px" p="0">
          <Box fontSize="14px" width="280px">
            <Flex align="flex-start" height="68px" p="8px 12px 0">
              <Box>
                <AccountIcon
                  width={48}
                  height={48}
                  color={colorMode}
                  className="big_avatar cursor_point"
                />
              </Box>
              <Box pl="8px" width={200}>
                <Box mb={4} mt={4}>
                  <TextEllipsis>
                    {loginUserModel.user?.loginName || ''}
                  </TextEllipsis>
                </Box>
                <Box>
                  <TextEllipsis placement="bottom">
                    {loginUserModel.user?.roleName === 'superAdministrator' ||
                    loginUserModel.user?.roleName === 'globalAdministrator'
                      ? t('header.roleName.' + loginUserModel.user?.roleName)
                      : loginUserModel.user?.roleName || ''}
                  </TextEllipsis>
                </Box>
              </Box>
            </Flex>

            <MenuDivider color="#000000" />
            <Box>
              <AccountSwitch />
            </Box>

            <MenuItem
              data-track="CasHeader.AccountSetting.openLanguageSetting"
              key="languageSetting"
              onClick={openLanguageSetting}
              {...menusItemStyle}
            >
              <Icon as={SphereIcon} />
              <Text ml={8}>{header_t('languageSetting')}</Text>
            </MenuItem>

            {isDefaultUI() && !isInUIC() && (
              <MenuItem
                data-track="CasHeader.AccountSetting.logOff"
                key="logOff"
                onClick={logoff}
                {...menusItemStyle}
              >
                <Icon as={UserExportOIcon} size="4x" />
                <Text ml={8}>{header_t('logOff')}</Text>
              </MenuItem>
            )}
          </Box>
        </PopoverContent>
      </Popover>
      <LanguageSetting
        languageSettingShow={languageSettingShow}
        closeLanguageSetting={closeLanguageSetting}
      />
    </>
  );
}

export default AccountSetting;
